/**
 * Who is waiting on a reply, derived from raw records.
 *
 * Pure by design: no React, no network. The rules here are the whole module,
 * and they are worth testing directly rather than through a component.
 */

import moment from 'moment'

export type ResponseChannel = `sms` | `call` | `voicemail` | `form` | `chat` | `scheduler`

/**
 * What kind of request an activity represents, if any.
 *
 * Read from the activity rather than the contact. `Contact.ts` only mirrors
 * these signals onto the contact when `lifecycleStage !== 'Patient'`, and
 * strips the tags entirely once someone converts, so a patient who leaves a
 * voicemail has nothing on their contact record. The activity is written the
 * same way for everyone.
 */
export function channelForActivity(type: string, tags: string[] = []): ResponseChannel | undefined {
  if (type === `Call`) {
    if (tags.includes(`Voicemail`)) return `voicemail`
    if (tags.includes(`Missed Call`)) return `call`
    return undefined
  }
  if (type === `Form`) return `form`
  if (type === `Chat`) return `chat`
  if (type === `Online Scheduler` || type === `Virtual Consultation`) return `scheduler`
  return undefined
}

/** What each way in is called, in the words the front desk already uses. */
export const CHANNEL_LABELS: Record<ResponseChannel, string> = {
  sms: `Text`,
  call: `Missed call`,
  voicemail: `Voicemail`,
  form: `Form`,
  chat: `Web chat`,
  scheduler: `Booking request`,
}

export type WaitTone = `fresh` | `warm` | `late`

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

/**
 * How hard the row should push. The queue exists to stop people being
 * abandoned, so the emphasis is on elapsed time rather than on who they are.
 *
 * An unparseable `since` fails toward urgency rather than toward calm: it
 * reads as `late`, not `fresh`.
 */
export function waitTone(since: string, now: Date = new Date()): WaitTone {
  const waited = now.getTime() - new Date(since).getTime()
  if (!Number.isFinite(waited)) return `late`
  if (waited >= DAY_MS) return `late`
  if (waited >= HOUR_MS) return `warm`
  return `fresh`
}

/**
 * Records arrive as ISO strings of unspecified precision, so compare instants
 * rather than text. Lexicographic order silently inverts when one timestamp
 * carries milliseconds and another does not.
 */
function compareAt(a: string, b: string): number {
  return new Date(a).getTime() - new Date(b).getTime()
}

/**
 * Something a member of staff did to a contact.
 *
 * An activity carrying an `owner` is a person's work: the feed is mostly
 * machine-written, and those rows are ownerless (`useLastTouch` measured 990 of
 * the 1,000 most recent as `Change` or `Patient` noise). The owner is therefore
 * the fingerprint that separates "a colleague dealt with this" from "the system
 * wrote a row".
 */
export type StaffTouch = {
  contactId: string
  at: string
  /** Who did it, when the record names them. */
  actor?: string
  /** The activity type, so the row can say what they did. */
  type?: string
}

/**
 * Did somebody already deal with this person, through a surface this queue
 * cannot see?
 *
 * The queue clears a row on the answers it knows about: an outbound message, an
 * outbound call, a `Connected` follow-up, an answered inbound call. Anything
 * else a colleague does leaves the person sitting on the list. A note, an
 * email, a booked appointment, a pipeline move: all real work, none of it
 * visible to the rules, so two people end up ringing the same patient.
 *
 * Measured against `latest` rather than `since`. `since` is their *oldest*
 * unanswered touch, so a colleague who replied last Tuesday would go on
 * clearing every call the person has made since. `latest` is their most recent
 * one, so somebody who reached out again after being dealt with correctly comes
 * back onto the list.
 *
 * Strictly after, never equal: the request that put them here can itself carry
 * an owner once it is assigned to somebody, and an assignment is not an answer.
 */
export function handledAfter(item: WaitingItem, touches: StaffTouch[]): StaffTouch | undefined {
  if (!item.contactId) return undefined
  return touches
    .filter((touch) => touch.contactId === item.contactId && compareAt(touch.at, item.latest) > 0)
    .sort((a, b) => compareAt(a.at, b.at))
    .pop()
}

type QueueBooking = {
  patientId?: string | null
  createdDate: string
  /** Day-granular: the PMS booking day, stored as UTC midnight. */
  appointmentMadeDate?: string | null
}

/** Longer than any sync lag seen on prod; a record created later than this is a backfill of history. */
const BACKFILL_LAG_MS = 2 * DAY_MS

/**
 * PMS bookings as work done on the person: they write no owned activity.
 *
 * Timed by `createdDate` because `appointmentMadeDate` carries no time and
 * cannot order a booking against a form sent the same day. Sync only lags, so a
 * booking recorded before the request was made before it too.
 */
export function bookingTouches(
  bookings: QueueBooking[],
  contacts: { id?: string; patientIds?: string[] | null }[],
): StaffTouch[] {
  const contactByPatient = new Map(
    contacts.flatMap(({ id, patientIds }) => (id ? (patientIds ?? []).map((patientId) => [patientId, id]) : [])),
  )
  return bookings.flatMap((booking) => {
    const contactId = booking.patientId ? contactByPatient.get(booking.patientId) : undefined
    if (!contactId) return []
    if (booking.appointmentMadeDate && compareAt(booking.createdDate, booking.appointmentMadeDate) > BACKFILL_LAG_MS) {
      return []
    }
    return [{ contactId, at: booking.createdDate, type: `Appointment` }]
  })
}

/**
 * The key an opt-out record is stored under: the last ten digits, nothing else.
 *
 * The API normalises it that way, so a number written `(207) 321-1866`,
 * `+12073211866` or `207.321.1866` all reduce to the same key. Anything with
 * fewer than ten digits cannot match a record and is not worth guessing at.
 */
export function optOutKey(phone: string | null | undefined): string | undefined {
  const digits = (phone ?? ``).replace(/\D/g, ``)
  return digits.length >= 10 ? digits.slice(-10) : undefined
}

/**
 * Has this person told the practice to stop texting them?
 *
 * They are not waiting on a reply, and there is no reply to give: once somebody
 * answers STOP the backend refuses every outbound message to that number, so a
 * row asking staff to text them back is asking for something impossible as well
 * as unlawful.
 *
 * Read from the practice's own opt-out records rather than by looking for STOP
 * in the message text. The record is what the sending path actually enforces,
 * and matching words would take "cancel" as an opt-out when a patient cancelling
 * an appointment is exactly who should stay on this list.
 */
export function hasOptedOut(phone: string | null | undefined, optedOutKeys: Set<string>): boolean {
  const key = optOutKey(phone)
  return key ? optedOutKeys.has(key) : false
}

export type QueueMessage = {
  id: string
  threadId?: string | null
  /** Null on a text from a number the practice has no record of. */
  contactId?: string | null
  from?: string | null
  locationId?: string | null
  /** Computed by the API: inbound (contact-initiated) or outbound. */
  direction?: string | null
  text: string
  read?: boolean | null
  createdDate: string
  /** Set when a person sent it. Present even when `direction` is not. */
  sentByUserId?: string | null
  /** True when the AI agent sent it. Present even when `direction` is not. */
  isAgent?: boolean | null
}

/**
 * Did this message come from our side?
 *
 * `direction` is computed and null on 83% of real messages, so it cannot be
 * the only test. Where it IS set it agrees exactly with these two fields: every
 * known-outbound message carries a sender or the agent flag, and no known-inbound
 * message carries either. So the sender is the reliable signal and `direction`
 * is a shortcut when present.
 *
 * Getting this wrong in the other direction would show people we have already
 * answered, including every AI agent reply, which by product decision clears a
 * row.
 */
function isOurReply(message: QueueMessage): boolean {
  if (message.direction === `outbound`) return true
  if (message.direction === `inbound`) return false
  return Boolean(message.sentByUserId) || message.isAgent === true
}

type ThreadedMessage = QueueMessage & { threadId: string }

/** The thread is the unit, not the contact: a text from an unsaved number has no `contactId` and still counts. */
function isThreadedMessage(message: QueueMessage): message is ThreadedMessage {
  return Boolean(message.threadId)
}

export type WaitingItem = {
  /**
   * The thread id for a text, the contact id for everything else. Stable
   * across polls: the React key, the selection, and what holds are filed under.
   */
  id: string
  /**
   * Id of the oldest unanswered touch (thread id for texts, activity id for
   * activities). This can change between fetches as touches arrive or clear,
   * so it is not a stable React key; `id` is.
   */
  key: string
  /** Absent on a text from a number with no contact record. */
  contactId?: string
  /** Texts only: the sending number and the office it reached, so the row is named and answered without a contact. */
  phone?: string
  locationId?: string
  channel: ResponseChannel
  /** One line of what they actually said. */
  preview: string
  /** When they first reached out without an answer. */
  since: string
  /**
   * Their most recent unanswered touch.
   *
   * `since` cannot stand in for it. That is the *oldest* touch, and it does not
   * move when somebody reaches out again, so a dismissal remembered against it
   * would go on hiding the row through every later call they made.
   */
  latest: string
  /** How many unanswered touches, so one person is one row. */
  touches: number
  unread: boolean
  /**
   * The tags on their most recent unanswered touch.
   *
   * On real data these are the only thing that tells one call from another:
   * every call activity in the practice carries the same description, "Inbound
   * Call", with an empty note. `informativeTags` picks out the ones worth
   * showing.
   */
  tags: string[]
  /**
   * The unanswered touches behind this row, newest last. Empty for texts,
   * whose thread is fetched live instead.
   *
   * `id` is the activity's own id, carried so a caller holding the records
   * these were derived from can match a touch back to the whole activity and
   * render it. Without it the row could only ever restate what it already
   * summarised, which is no reason to open it.
   */
  events: { id: string; at: string; description: string; tags: string[] }[]
}

/**
 * Threads whose most recent message is inbound.
 *
 * Unread is not the test. Reading a text is not answering it, and someone
 * skimming the inbox on a phone would otherwise clear the row without
 * replying. Unread rides along as emphasis instead.
 *
 * Any outbound message clears the thread, including one sent by the AI agent.
 * That is a deliberate product decision: see the accepted risk in the spec.
 */
export function waitingFromMessages(messages: QueueMessage[]): WaitingItem[] {
  const threads = new Map<string, ThreadedMessage[]>()
  for (const message of messages) {
    if (!isThreadedMessage(message)) continue
    const bucket = threads.get(message.threadId) ?? []
    bucket.push(message)
    threads.set(message.threadId, bucket)
  }

  const items: WaitingItem[] = []
  for (const [threadId, bucket] of threads) {
    // At an equal instant our reply sorts first, so a message tied with our
    // reply still lands as the latest touch rather than the reply itself.
    const ordered = [...bucket].sort(
      (a, b) => compareAt(a.createdDate, b.createdDate) || Number(isOurReply(b)) - Number(isOurReply(a)),
    )
    const latest = ordered[ordered.length - 1]
    if (isOurReply(latest)) continue

    // Everything since their last answered message is one wait, so the clock
    // starts at the first of them rather than the most recent. A message tied
    // with our reply is still counted as unanswered.
    const lastOurReply = ordered.filter(isOurReply).pop()
    const unanswered = ordered.filter(
      (m) => !isOurReply(m) && (!lastOurReply || compareAt(m.createdDate, lastOurReply.createdDate) >= 0),
    )
    items.push({
      id: threadId,
      key: threadId,
      // Duplicate contacts share a number, so one thread can name several; the latest is the one last on screen.
      contactId: latest.contactId ?? undefined,
      phone: latest.from ?? undefined,
      locationId: latest.locationId ?? undefined,
      channel: `sms`,
      preview: latest.text,
      since: unanswered[0].createdDate,
      latest: latest.createdDate,
      touches: unanswered.length,
      unread: unanswered.some((m) => m.read !== true),
      // A text says what it says; nothing tags it.
      tags: [],
      // Deliberately empty. A text row expands into the live thread, which is
      // fetched at the moment it opens, so a frozen copy of the same messages
      // taken at poll time would only be a second, staler answer.
      events: [],
    })
  }
  return items
}

export type QueueActivity = {
  id: string
  contactId?: string | null
  type: string
  /** For calls this is the direction: `Activity.ts` sets `subType: call.direction`. */
  subType?: string | null
  tags?: string[] | null
  description?: string | null
  /** `Maybe<DateTime>` in the schema: seen null in real data, so records without one are skipped. */
  activityDate?: string | null
}

/** The tags that say a call never became a conversation. */
const UNANSWERED_CALL_TAGS = [`Missed Call`, `Voicemail`, `Abandoned Call`]

/** Tags about our own recording rather than about the person waiting. */
const PLUMBING_TAGS = [`No Recording`, `No Patient Found`]

/** Read these first when they are there. Everything else keeps its own order. */
const TAG_PRIORITY = [`New Patient`, `Current Patient`]

function tagRank(tag: string): number {
  const rank = TAG_PRIORITY.indexOf(tag)
  return rank === -1 ? TAG_PRIORITY.length : rank
}

/**
 * The tags on a touch that are worth a glance.
 *
 * The middle of the row used to show the activity's description, which on a
 * real practice is the string "Inbound Call" on all 510 of them. The tags are
 * the only thing that varies, and a new patient calling is the most valuable
 * row on the board, so they earn the space the description was wasting.
 *
 * Two kinds are dropped. The channel tags are already stated by the heading of
 * the section the row sits under, and the plumbing tags describe our recording
 * rather than their call. Everything else is kept as written, because practices
 * add their own and we cannot know which of those matter.
 */
export function informativeTags(tags: string[]): string[] {
  return (
    tags
      .filter((tag) => !UNANSWERED_CALL_TAGS.includes(tag) && !PLUMBING_TAGS.includes(tag))
      // Sorting is stable per spec, so an unranked tag keeps the order it came in.
      .sort((a, b) => tagRank(a) - tagRank(b))
  )
}

function isResponse(activity: QueueActivity): boolean {
  // A follow-up only answers someone if it reached them. Logging a callback
  // that went to voicemail is not a reply, and treating it as one would drop
  // the person from the only list that would have caught them.
  if (activity.type === `FollowUp`) return activity.tags?.includes(`Connected`) === true
  if (activity.type !== `Call`) return false
  if (activity.subType === `outbound`) return true
  // The person rang back and somebody picked up. That is the commonest way a
  // missed call actually gets answered at a practice whose phone system logs
  // nothing outbound, and it leaves no other trace: an inbound call carrying
  // none of the unanswered tags is a conversation that happened.
  return !(activity.tags ?? []).some((tag) => UNANSWERED_CALL_TAGS.includes(tag))
}

/**
 * Inbound requests with nothing logged after them.
 *
 * One row per contact, timed from the oldest unanswered request, so someone
 * who called and then filled in a form is one person to ring back rather than
 * two rows to clear.
 */
export function waitingFromActivities(activities: QueueActivity[]): WaitingItem[] {
  const byContact = new Map<string, QueueActivity[]>()
  for (const activity of activities) {
    if (!activity.contactId || !activity.activityDate) continue
    const bucket = byContact.get(activity.contactId) ?? []
    bucket.push(activity)
    byContact.set(activity.contactId, bucket)
  }

  const items: WaitingItem[] = []
  for (const [contactId, bucket] of byContact) {
    // At an equal instant a response sorts first, matching the message side:
    // a request tied with its response still lands as the latest touch.
    const ordered = [...bucket].sort(
      (a, b) =>
        compareAt(a.activityDate as string, b.activityDate as string) || Number(isResponse(b)) - Number(isResponse(a)),
    )
    const lastResponse = ordered.filter(isResponse).pop()
    const waiting = ordered.flatMap((activity) => {
      if (activity.subType === `outbound`) return []
      const channel = channelForActivity(activity.type, activity.tags ?? [])
      if (!channel) return []
      if (lastResponse && compareAt(activity.activityDate as string, lastResponse.activityDate as string) < 0) {
        return []
      }
      return [{ activity, channel }]
    })
    if (!waiting.length) continue

    const first = waiting[0]
    const latest = waiting[waiting.length - 1]
    items.push({
      id: contactId,
      key: first.activity.id,
      contactId,
      channel: latest.channel,
      preview: latest.activity.description ?? ``,
      since: first.activity.activityDate as string,
      latest: latest.activity.activityDate as string,
      touches: waiting.length,
      // Activities carry no read state; the row is new work either way.
      unread: true,
      tags: latest.activity.tags ?? [],
      // Every touch, not just the one the row summarises. Three unanswered
      // missed calls collapse to one row on purpose, and this is the only place
      // that still knows there were three of them.
      events: waiting.map(({ activity }) => ({
        id: activity.id,
        at: activity.activityDate as string,
        description: activity.description ?? ``,
        tags: activity.tags ?? [],
      })),
    })
  }
  return items
}

/**
 * One row per id, longest waiting first.
 *
 * Someone who called twice and filled in a form is one person to ring back,
 * and the clock runs from the first thing nobody answered. A text thread is
 * its own row beside that: the count of texts is a count of threads.
 */
export function mergeWaiting(items: WaitingItem[]): WaitingItem[] {
  const byId = new Map<string, WaitingItem>()
  for (const item of items) {
    const existing = byId.get(item.id)
    if (!existing) {
      byId.set(item.id, { ...item })
      continue
    }
    const oldest = compareAt(item.since, existing.since) < 0 ? item : existing
    const newest = compareAt(item.latest, existing.latest) > 0 ? item : existing
    byId.set(item.id, {
      // The spread keeps the channel and its tags together. They describe one
      // touch, and a badge from one with the tags of another would be a lie.
      ...oldest,
      latest: newest.latest,
      touches: existing.touches + item.touches,
      unread: existing.unread || item.unread,
      // Every touch from both sides, not the oldest side's alone: the expanded
      // row is the only place somebody can see what they actually did. Sorted
      // rather than concatenated blind, so two lists that were each newest-last
      // do not merge into one that is neither.
      events: [...existing.events, ...item.events].sort((a, b) => compareAt(a.at, b.at)),
    })
  }
  return [...byId.values()].sort((a, b) => compareAt(a.since, b.since))
}

/**
 * What a kind of waiting is called when you are counting several of them.
 *
 * Held apart from `channelLabel`, which names one touch on one row. These are
 * the words the summary sentence and the section headings both speak, and they
 * have to be the same words: a sentence promising voicemails above a heading
 * that called them something else would read as two lists rather than one.
 */
const CHANNEL_NOUNS: Record<ResponseChannel, { one: string; many: string }> = {
  // "Unanswered", not "unread". This module's whole rule is that reading a text
  // is not answering it, so a read-but-unanswered text sits here too. Calling
  // the section "unread" would contradict the reason the row is in it, and the
  // count would sometimes exceed the unread dots beneath it.
  sms: { one: `unanswered text`, many: `unanswered texts` },
  voicemail: { one: `voicemail`, many: `voicemails` },
  scheduler: { one: `booking request`, many: `booking requests` },
  form: { one: `form`, many: `forms` },
  call: { one: `missed call`, many: `missed calls` },
  chat: { one: `web chat`, many: `web chats` },
}

/**
 * Which kind of waiting is read first. Highest intent at the top.
 *
 * Deliberately neither alphabetical nor by volume, so please do not reshuffle
 * it into either. A text is somebody mid-conversation who is actively waiting,
 * and it is also the fastest row to clear. A voicemail states its intent out
 * loud: they left a message. A booking request is somebody trying to take a
 * time, which is the closest of these to production. A form is a written
 * enquiry, usually from a new patient. Missed calls come next because they are
 * the bulk of the list and carry the least information per row, so leading with
 * them would bury everything that says more. A web chat is the rarest.
 */
const CHANNEL_ORDER: ResponseChannel[] = [`sms`, `voicemail`, `scheduler`, `form`, `call`, `chat`]

/**
 * Generic over the item so grouping hands back whatever it was given. The panel
 * groups rows that carry a contact alongside the waiting item, and a signature
 * fixed to `WaitingItem` would quietly strip that on the way through.
 */
export type ChannelGroup<T extends WaitingItem = WaitingItem> = {
  channel: ResponseChannel
  label: string
  items: T[]
}

/** One list of words to keep in step rather than two: the heading is the counting noun, capitalised. */
function heading(channel: ResponseChannel): string {
  const noun = CHANNEL_NOUNS[channel].many
  return `${noun[0].toUpperCase()}${noun.slice(1)}`
}

/**
 * The list split into the kinds of work it is made of.
 *
 * A flat list makes somebody work out what each row is one row at a time. A
 * heading answers that once for everything beneath it, and the kinds really are
 * different jobs: replying to a text is not the motion that rings a number back.
 *
 * Order within a group is left exactly as it arrives, because the caller has
 * already sorted it by wait or by recency. Grouping chooses the sections; it
 * never re-chooses the rows.
 */
export function groupByChannel<T extends WaitingItem>(items: T[]): ChannelGroup<T>[] {
  return CHANNEL_ORDER.flatMap((channel) => {
    const mine = items.filter((item) => item.channel === channel)
    // A heading with nothing under it is work that does not exist, and six of
    // them would make a quiet morning look like a full one.
    if (!mine.length) return []
    return [{ channel, label: heading(channel), items: mine }]
  })
}

/**
 * Which row to hand somebody once they have dealt with this one.
 *
 * The panel is a pile to clear, so finishing with a row should hand over the
 * next one rather than the empty list you started from. `ordered` is the list
 * as it reads on screen, already flattened across its channel headings, so a
 * row at the bottom of one section hands over to the top of the next: a
 * heading groups the pile, it does not divide it.
 *
 * At the end of the list there is nothing below, so it turns round and hands
 * back the row above. Only an empty list ends the run.
 */
export function nextAfterRemoval<T extends { id: string }>(ordered: T[], removedId: string): string | undefined {
  const index = ordered.findIndex((item) => item.id === removedId)
  // Already gone, most likely cleared by a poll or by somebody else. Handing
  // over a guess would open a row nobody asked for.
  if (index === -1) return undefined
  return (ordered[index + 1] ?? ordered[index - 1])?.id
}

/**
 * What is waiting, in one sentence.
 *
 * This is read before anything else on the panel, so it is a sentence rather
 * than a row of counters: somebody glancing at the page on their way past can
 * take a sentence in without stopping to parse a layout. The clauses run in the
 * same order as the sections below, so the sentence reads as a table of
 * contents for them rather than as a second, competing account.
 */
export function summarise(items: WaitingItem[]): string {
  const clauses = groupByChannel(items).map((group) => {
    const noun = CHANNEL_NOUNS[group.channel]
    return `${group.items.length} ${group.items.length === 1 ? noun.one : noun.many}`
  })
  if (!clauses.length) return ``
  if (clauses.length === 1) return clauses[0]
  // No Oxford comma. This is a spoken sentence, not a list of items.
  return `${clauses.slice(0, -1).join(`, `)} and ${clauses[clauses.length - 1]}`
}

/**
 * The lengths of "not now" worth offering. Anything finer is a calendar.
 *
 * Owned here beside the dismissal rules because it is queue vocabulary: the
 * row's own snooze menu and the modal's action bar both speak it, and two
 * copies would drift into offering different lengths for the same act.
 */
export const SNOOZE_OPTIONS: { label: string; at: () => Date }[] = [
  { label: `Later today`, at: () => moment().add(3, `hours`).toDate() },
  { label: `Tomorrow morning`, at: () => moment().add(1, `day`).startOf(`day`).add(8, `hours`).toDate() },
  { label: `Next week`, at: () => moment().add(7, `days`).startOf(`day`).add(8, `hours`).toDate() },
]

/**
 * A row somebody took off the list by hand.
 *
 * Some rows can never clear from data alone, so this is the only escape hatch
 * and it has to survive a reload. It is deliberately not a mute: it remembers
 * the touch it was applied to, and a newer one brings the row straight back.
 * A permanent per-contact mute would silently swallow a future patient.
 */
export type Dismissal = {
  /** The newest touch that was taken off the list. */
  latest: string
  /**
   * Hidden only until this instant, for a message left on voicemail. They are
   * still waiting, so the row drops off today's list rather than clearing.
   * Absent means hidden outright.
   */
  until?: string
}

export function isDismissed(record: Dismissal | undefined, item: WaitingItem, now: Date = new Date()): boolean {
  if (!record) return false
  if (compareAt(item.latest, record.latest) > 0) return false
  if (!record.until) return true
  return now.getTime() < new Date(record.until).getTime()
}

/** What the carrier sends when it has no name to send. */
const CALLER_ID_PLACEHOLDERS = [`wireless caller`, `unknown`, `unavailable`, `toll free`, `private caller`]

const STATE_CODES = new Set(
  `al ak az ar ca co ct de dc fl ga hi id il in ia ks ky la me md ma mi mn ms mo mt ne nv nh nj nm ny nc nd oh ok or pa ri sc sd tn tx ut vt va wa wv wi wy`.split(
    ` `,
  ),
)

/**
 * Is this string a caller-ID artifact rather than a person?
 *
 * Real queues offer "Wireless Caller" and "San Antonio Tx" as people to ring
 * back. They did call, so the row stays; what has to stop is presenting the
 * switchboard's own filler as somebody's name.
 *
 * Judged conservatively on purpose. Only known carrier placeholders and a
 * strict "City ST" shape count, because the cost of a false positive is showing
 * a real patient's number where their name should be.
 */
export function isCallerIdName(name: string | null | undefined): boolean {
  const cleaned = name?.trim().toLowerCase()
  if (!cleaned) return false
  if (CALLER_ID_PLACEHOLDERS.includes(cleaned)) return true
  const words = cleaned.split(/\s+/)
  if (words.length < 2) return false
  if (!STATE_CODES.has(words[words.length - 1])) return false
  return words.slice(0, -1).every((word) => /^[a-z][a-z.'-]*$/.test(word))
}
